import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { ArrowLeft, Briefcase, Phone, MapPin, User, Users, Building2, Printer, Banknote, ExternalLink, Eye, IndianRupee, CreditCard } from 'lucide-react';
import PhotoLightbox from '../components/PhotoLightbox';

const EntrepreneurDetail = () => {
  const { entrepreneurId } = useParams();
  const navigate = useNavigate();
  const [entrepreneur, setEntrepreneur] = useState(null);
  const [branch, setBranch] = useState(null);
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [photoOpen, setPhotoOpen] = useState(false);

  useEffect(() => {
    fetchEntrepreneurDetails();
  }, [entrepreneurId]);

  const fetchEntrepreneurDetails = async () => {
    try {
      const [entrepreneurRes, branchesRes, loansRes] = await Promise.all([
        api.get(`/entrepreneurs/${entrepreneurId}`),
        api.get('/branches'),
        api.get('/loans')
      ]);
      setEntrepreneur(entrepreneurRes.data);
      const entBranch = branchesRes.data.find(b => b.id === entrepreneurRes.data.branch_id);
      setBranch(entBranch);
      // Filter loans for this entrepreneur
      const entLoans = loansRes.data.filter(l => l.entrepreneur_id === entrepreneurId);
      setLoans(entLoans);
    } catch (error) {
      toast.error('Failed to fetch entrepreneur details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  if (!entrepreneur) {
    return (
      <div className="p-6 md:p-8 lg:p-10">
        <div className="text-center py-12">
          <p className="text-slate-500">Entrepreneur not found</p>
          <Button onClick={() => navigate('/entrepreneurs')} className="mt-4">
            Back to Entrepreneurs
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 lg:p-10" data-testid="entrepreneur-detail-page">
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate('/entrepreneurs')}
          className="mb-4 print:hidden"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Entrepreneurs
        </Button>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <h1 className="text-4xl font-bold text-slate-900">Entrepreneur Details</h1>
          <div className="flex gap-2 print:hidden">
            <Button
              variant="outline"
              onClick={() => navigate(`/entrepreneurs/${entrepreneurId}/livelihoods`)}
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              data-testid="view-livelihood-report-btn"
            >
              <Briefcase className="w-4 h-4 mr-2" />
              View Livelihood Report
            </Button>
            <Button
              variant="outline"
              onClick={() => window.print()}
              data-testid="print-entrepreneur-btn"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
          </div>
        </div>
      </div>

      {/* Profile Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Photo Card */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <div className="flex flex-col items-center">
            {entrepreneur.photo_url ? (
              <img
                src={entrepreneur.photo_url.startsWith('data:') ? entrepreneur.photo_url : `${process.env.REACT_APP_BACKEND_URL}${entrepreneur.photo_url}`}
                alt={entrepreneur.name}
                className="w-36 h-44 rounded-lg object-cover border-2 border-emerald-100 shadow-md mb-4 cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => setPhotoOpen(true)}
                data-testid="entrepreneur-photo-thumbnail"
              />
            ) : (
              <div className="w-36 h-44 rounded-lg bg-slate-200 flex items-center justify-center text-slate-600 font-bold text-6xl mb-4 border-2 border-slate-300">
                {entrepreneur.name.charAt(0)}
              </div>
            )}

            {entrepreneur.photo_url && (
              <PhotoLightbox
                src={entrepreneur.photo_url.startsWith('data:') ? entrepreneur.photo_url : `${process.env.REACT_APP_BACKEND_URL}${entrepreneur.photo_url}`}
                alt={entrepreneur.name}
                open={photoOpen}
                onClose={() => setPhotoOpen(false)}
              />
            )}
            <h2 className="text-2xl font-bold text-slate-900 text-center">{entrepreneur.name}</h2>
            {entrepreneur.enrollment_no && (
              <span className="mt-2 text-xs px-3 py-1 rounded-full font-medium bg-blue-100 text-blue-800 border border-blue-200">
                {entrepreneur.enrollment_no}
              </span>
            )}
            <p className="text-sm text-slate-600 mt-1">{entrepreneur.gender}</p>
            <div className="flex flex-wrap gap-2 mt-3 justify-center">
              <span className="text-xs px-3 py-1 rounded-full font-medium bg-emerald-100 text-emerald-800 border border-emerald-200">
                {entrepreneur.caste}
              </span>
              {branch && (
                <span className="text-xs px-3 py-1 rounded-full font-medium bg-purple-100 text-purple-800 border border-purple-200 flex items-center gap-1">
                  <Building2 className="w-3 h-3" />
                  {branch.name}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-emerald-700" />
            Contact Information
          </h3>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Phone className="w-5 h-5 text-slate-400 mt-0.5" />
              <div>
                <p className="text-xs text-slate-500">Mobile Number</p>
                <p className="text-base font-medium text-slate-900">{entrepreneur.mobile}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
              <div>
                <p className="text-xs text-slate-500">Address</p>
                <p className="text-base font-medium text-slate-900">{entrepreneur.address}</p>
              </div>
            </div>
            {(entrepreneur.district || entrepreneur.state || entrepreneur.block || entrepreneur.gram_panchayat || entrepreneur.village || entrepreneur.pincode) && (
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-500">Location</p>
                  <p className="text-base font-medium text-slate-900">
                    {[entrepreneur.village, entrepreneur.gram_panchayat, entrepreneur.block, entrepreneur.district, entrepreneur.state, entrepreneur.pincode].filter(Boolean).join(', ') || 'N/A'}
                  </p>
                </div>
              </div>
            )}
            {entrepreneur.pan_number && (
              <div className="flex items-start gap-3">
                <CreditCard className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-500">PAN Number</p>
                  <p className="text-base font-medium text-slate-900">{entrepreneur.pan_number}</p>
                </div>
              </div>
            )}
            {entrepreneur.aadhaar_number && (
              <div className="flex items-start gap-3">
                <CreditCard className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-500">Aadhaar Number</p>
                  <p className="text-base font-medium text-slate-900">{entrepreneur.aadhaar_number}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* SHG / VO / CLF Information */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-emerald-700" />
            SHG / VO / CLF Information
          </h3>
          <div className="space-y-3">
            <div>
              <p className="text-xs text-slate-500">Name of SHG</p>
              <p className="text-base font-medium text-slate-900">{entrepreneur.shg_vlf_name || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Name of VO</p>
              <p className="text-base font-medium text-slate-900">{entrepreneur.vo_name || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Name of CLF</p>
              <p className="text-base font-medium text-slate-900">{entrepreneur.clf_name || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">SHG Member Name</p>
              <p className="text-base font-medium text-slate-900">{entrepreneur.shg_member_name}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Relationship</p>
              <p className="text-base font-medium text-slate-900">{entrepreneur.shg_member_relation}</p>
            </div>
            {entrepreneur.crp_ep_name && (
              <div className="pt-2 border-t border-slate-100">
                <p className="text-xs text-slate-500">Promoted by CRP-EP</p>
                <p className="text-base font-medium text-emerald-700">{entrepreneur.crp_ep_name}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Enterprise Information */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <h3 className="text-xl font-semibold text-slate-900 mb-6 flex items-center gap-2">
          <Briefcase className="w-6 h-6 text-emerald-700" />
          Enterprise Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <p className="text-sm text-slate-500 mb-1">Enterprise Name</p>
            <p className="text-lg font-semibold text-slate-900">{entrepreneur.enterprise.name}</p>
          </div>
          <div>
            <p className="text-sm text-slate-500 mb-1">Enterprise Type</p>
            <p className="text-lg font-semibold text-slate-900">{entrepreneur.enterprise.type}</p>
          </div>
          <div>
            <p className="text-sm text-slate-500 mb-1">Operation Type</p>
            <span className="inline-block text-xs px-3 py-1 rounded-full font-medium bg-emerald-100 text-emerald-800 border border-emerald-200">
              {entrepreneur.enterprise.operation_type}
            </span>
          </div>
          <div>
            <p className="text-sm text-slate-500 mb-1">Status</p>
            <span className={`inline-block text-xs px-3 py-1 rounded-full font-medium ${
              entrepreneur.enterprise.is_existing
                ? 'bg-blue-100 text-blue-800 border border-blue-200'
                : 'bg-green-100 text-green-800 border border-green-200'
            }`}>
              {entrepreneur.enterprise.is_existing ? 'Existing' : 'New'}
            </span>
          </div>
          {entrepreneur.enterprise.loan_type && (
            <div>
              <p className="text-sm text-slate-500 mb-1">Proposed Loan Type</p>
              <p className="text-lg font-semibold text-purple-700">{entrepreneur.enterprise.loan_type}</p>
            </div>
          )}
          {entrepreneur.enterprise.proposed_loan_amount > 0 && (
            <div>
              <p className="text-sm text-slate-500 mb-1">Proposed Loan Amount</p>
              <p className="text-lg font-semibold text-slate-900">
                ₹{entrepreneur.enterprise.proposed_loan_amount.toLocaleString('en-IN')}
              </p>
            </div>
          )}
          {entrepreneur.enterprise.product_item && (
            <div>
              <p className="text-sm text-slate-500 mb-1">Product / Item</p>
              <p className="text-base font-medium text-slate-900">{entrepreneur.enterprise.product_item}</p>
            </div>
          )}
          {entrepreneur.enterprise.ownership_structure && (
            <div>
              <p className="text-sm text-slate-500 mb-1">Ownership Structure</p>
              <p className="text-base font-medium text-slate-900">{entrepreneur.enterprise.ownership_structure}</p>
            </div>
          )}
          {entrepreneur.enterprise.govt_license && (
            <div>
              <p className="text-sm text-slate-500 mb-1">Govt. License</p>
              <p className="text-base font-medium text-slate-900">{entrepreneur.enterprise.govt_license}</p>
            </div>
          )}
        </div>
      </div>

      {/* Loan Accounts */}
      <div className="mt-6 bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-center gap-2 mb-4">
          <Banknote className="w-5 h-5 text-purple-600" />
          <h3 className="text-lg font-semibold text-slate-900">Loan Accounts ({loans.length})</h3>
        </div>
        {loans.length > 0 ? (
          <div className="space-y-3">
            {loans.map((loan) => (
              <div
                key={loan.id}
                className="p-4 rounded-lg border border-slate-200 hover:border-purple-200 transition-all"
                data-testid={`loan-account-${loan.loan_id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      loan.status === 'active' ? 'bg-emerald-100 text-emerald-700' : loan.status === 'closed' ? 'bg-slate-100 text-slate-600' : 'bg-red-100 text-red-700'
                    }`}>
                      <Banknote className="w-5 h-5" />
                    </div>
                    <div>
                      <Link to={`/loans/${loan.id}`} className="font-semibold text-purple-700 hover:text-purple-900 hover:underline">
                        {loan.loan_id}
                      </Link>
                      <p className="text-xs text-slate-500">{loan.loan_type} | Disbursed: {new Date(loan.disbursement_date).toLocaleDateString('en-IN')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm font-bold text-slate-900">₹{loan.loan_amount.toLocaleString('en-IN')}</p>
                      <p className="text-xs text-red-600">Outstanding: ₹{loan.outstanding_amount.toLocaleString('en-IN')}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                      loan.status === 'active' ? 'bg-emerald-100 text-emerald-800' : loan.status === 'closed' ? 'bg-slate-100 text-slate-700' : 'bg-red-100 text-red-800'
                    }`}>{loan.status}</span>
                  </div>
                </div>
                <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
                  <Link to={`/loans/${loan.id}`} className="text-xs px-3 py-1.5 rounded bg-purple-100 text-purple-700 hover:bg-purple-200 font-medium flex items-center gap-1">
                    <Eye className="w-3 h-3" /> Loan Details
                  </Link>
                  {loan.status === 'active' && (
                    <Link to={`/loans/${loan.id}`} className="text-xs px-3 py-1.5 rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200 font-medium flex items-center gap-1">
                      <IndianRupee className="w-3 h-3" /> Record Repayment
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500 italic">No loan accounts found for this entrepreneur</p>
        )}
      </div>

      {/* Metadata */}
      <div className="mt-6 bg-slate-50 rounded-xl border border-slate-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-slate-600">
          <div>
            <span className="font-medium">Enrolled on:</span>{' '}
            {new Date(entrepreneur.created_at).toLocaleDateString('en-IN', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
          </div>
          <div>
            <span className="font-medium">Last updated:</span>{' '}
            {new Date(entrepreneur.updated_at).toLocaleDateString('en-IN', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EntrepreneurDetail;
